import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import NavigationMenu from '../components/NavigationMenu';
import UserMenu from '../components/UserMenu';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { 
  Zap, Check, X, Moon, Sun, Crown, Sparkles, Shield,
  Infinity, ArrowRight, Tag, ChevronDown, ChevronUp, Loader2,
  Calendar, CreditCard, ShieldCheck, Star, Clock, Gift
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackUpgradeClicked } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PricingPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [promoExpanded, setPromoExpanded] = useState(false);
  const [promoApplied, setPromoApplied] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [applyingReferral, setApplyingReferral] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Lifetime Pro state
  const [lifetimeProStatus, setLifetimeProStatus] = useState(null);
  const [lifetimeLoading, setLifetimeLoading] = useState(false);

  const currentPlan = user?.subscription?.tier || 'free';

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 300);
    
    // Fetch Lifetime Pro status
    const fetchLifetimeStatus = async () => {
      try {
        const response = await axios.get(`${API}/api/billing/lifetime-pro/status`);
        setLifetimeProStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch Lifetime Pro status:', error);
      }
    };
    fetchLifetimeStatus();
    
    return () => clearTimeout(timer);
  }, []);

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    setApplyingPromo(true);
    try {
      const response = await axios.post(
        `${API}/api/billing/validate-promo`,
        { code: promoCode.trim().toUpperCase() },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      if (response.data.valid) {
        setPromoApplied(response.data);
        toast.success(`Promo applied! ${response.data.description}`);
      } else {
        toast.error(response.data.message || 'Invalid promo code');
      }
    } catch (error) {
      toast.error('Invalid promo code');
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleApplyReferral = async () => {
    if (!referralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    setApplyingReferral(true);
    try {
      const response = await axios.post(
        `${API}/api/billing/validate-promo`,
        { code: referralCode.trim().toUpperCase() },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      if (response.data.valid) {
        setPromoApplied(response.data);
        toast.success(`Referral applied! ${response.data.description}`);
      } else {
        toast.error(response.data.message || 'Invalid referral code');
      }
    } catch (error) {
      toast.error('Invalid referral code');
    } finally {
      setApplyingReferral(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    if (planId === 'free') {
      if (!user) navigate('/auth?mode=signup');
      return;
    }
    if (!user) {
      toast.info('Please sign in to subscribe');
      navigate('/auth?mode=signin');
      return;
    }
    trackUpgradeClicked(planId, billingInterval);
    setSelectedPlan(planId);
    setShowTrialModal(true);
  };

  const proceedToCheckout = async () => {
    if (!selectedPlan) return;
    setShowTrialModal(false);
    setCheckoutLoading(selectedPlan);
    try {
      const response = await axios.post(
        `${API}/api/billing/checkout`,
        {
          plan: selectedPlan,
          interval: billingInterval,
          promo_code: promoApplied?.code || null,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleLifetimeProCheckout = async () => {
    if (!user) {
      toast.info('Please sign in to purchase Lifetime Pro');
      navigate('/auth?mode=signin');
      return;
    }
    
    if (currentPlan === 'lifetime_pro') {
      toast.info('You already have Lifetime Pro!');
      return;
    }
    
    setLifetimeLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/billing/lifetime-pro/checkout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Lifetime Pro checkout error:', error);
      const message = error.response?.data?.detail || 'Failed to start checkout. Please try again.';
      toast.error(message);
    } finally {
      setLifetimeLoading(false);
    }
  };

  const scrollToCompare = () => {
    const element = document.getElementById('compare-features');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const getPrice = (monthly, annual) => {
    if (billingInterval === 'annual') {
      const discounted = promoApplied?.discount_percent 
        ? annual * (1 - promoApplied.discount_percent / 100)
        : annual;
      return { price: discounted, original: annual, perMonth: (discounted / 12).toFixed(2) };
    }
    const discounted = promoApplied?.discount_percent 
      ? monthly * (1 - promoApplied.discount_percent / 100)
      : monthly;
    return { price: discounted, original: monthly, perMonth: discounted.toFixed(2) };
  };

  // New pricing: Basic $4.99/mo or $49/yr, Pro $9.99/mo or $99/yr
  const basicPrice = getPrice(4.99, 49);
  const proPrice = getPrice(9.99, 99);

  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + (selectedPlan === 'pro' ? 7 : 3));
  const formattedTrialEnd = trialEndDate.toLocaleDateString('en-US', { 
    month: 'long', day: 'numeric', year: 'numeric' 
  });

  const getSelectedPlanPrice = () => {
    if (!selectedPlan) return { price: 0, name: '' };
    const prices = selectedPlan === 'basic' ? basicPrice : proPrice;
    const name = selectedPlan === 'basic' ? 'Basic' : 'Pro';
    return { 
      price: billingInterval === 'annual' ? prices.price.toFixed(0) : prices.price.toFixed(2),
      interval: billingInterval === 'annual' ? 'year' : 'month',
      name 
    };
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Trial Confirmation Modal */}
      {showTrialModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold mb-2">Start Your Free Trial</h2>
              <p className="text-muted-foreground text-sm">
                Try {getSelectedPlanPrice().name} free for {selectedPlan === 'pro' ? '7' : '3'} days
              </p>
            </div>
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3 p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl border border-emerald-200 dark:border-emerald-800">
                <Calendar className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-emerald-800 dark:text-emerald-200">No charge today</p>
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">
                    Your {selectedPlan === 'pro' ? '7' : '3'}-day free trial starts now. You will not be charged until <span className="font-semibold">{formattedTrialEnd}</span>.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-secondary/50 rounded-xl">
                <CreditCard className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">After trial ends</p>
                  <p className="text-sm text-muted-foreground">
                    You will be charged <span className="font-semibold text-foreground">${getSelectedPlanPrice().price}/{getSelectedPlanPrice().interval}</span>. Cancel anytime before the trial ends to avoid charges.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-secondary/50 rounded-xl">
                <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Easy cancellation</p>
                  <p className="text-sm text-muted-foreground">Cancel anytime from your account settings. No questions asked.</p>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Button onClick={proceedToCheckout} disabled={checkoutLoading} className="w-full h-12 bg-primary hover:bg-primary/90 text-lg font-semibold">
                {checkoutLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue to Payment'}
              </Button>
              <button onClick={() => setShowTrialModal(false)} className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                Cancel
              </button>
            </div>
            <p className="text-center text-xs text-muted-foreground mt-4">🔒 Secure payment powered by Stripe</p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <NavigationMenu />
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg">FlipValue</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-6 sm:py-8">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">Simple, Transparent Pricing</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Start free, upgrade when you need more</p>
        </div>

        {/* Billing Toggle */}
        <div className="sticky top-[57px] z-40 bg-background/95 backdrop-blur py-4 -mx-4 px-4 mb-2">
          <div className="flex justify-center">
            <div className="inline-flex items-center bg-secondary/80 rounded-full p-1.5 shadow-sm border border-border">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${billingInterval === 'monthly' ? 'bg-background text-foreground shadow-md' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('annual')}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${billingInterval === 'annual' ? 'bg-background text-foreground shadow-md' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Annual
                <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full">-23%</span>
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="max-w-xl mx-auto space-y-4 sm:space-y-6 mb-6 sm:mb-8">
            
            {/* Promo Code Section */}
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <button onClick={() => setPromoExpanded(!promoExpanded)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-secondary/50 transition-colors">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Have a promo or referral code?</span>
                </div>
                {promoExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>
              {promoExpanded && (
                <div className="px-4 pb-4 pt-2 border-t border-border space-y-4">
                  {promoApplied ? (
                    <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg">
                      <div>
                        <p className="font-medium text-primary">{promoApplied.code}</p>
                        <p className="text-xs text-muted-foreground">{promoApplied.description}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => { setPromoApplied(null); setPromoCode(''); setReferralCode(''); toast.info('Code removed'); }}>
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Promo Code</label>
                        <div className="flex gap-2">
                          <Input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} placeholder="e.g., WELCOME10" className="flex-1 uppercase" maxLength={20} />
                          <Button onClick={handleApplyPromo} disabled={applyingPromo || !promoCode.trim()} variant="outline" size="sm">
                            {applyingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                          </Button>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Referral Code</label>
                        <div className="flex gap-2">
                          <Input value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder="Friend's referral code" className="flex-1 uppercase" maxLength={20} />
                          <Button onClick={handleApplyReferral} disabled={applyingReferral || !referralCode.trim()} variant="outline" size="sm">
                            {applyingReferral ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Free Plan */}
            <div className="bg-card rounded-xl sm:rounded-2xl border border-border p-5 sm:p-6 transition-all hover:shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-xl bg-secondary text-muted-foreground">
                  <Shield className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Free</h3>
                  <span className="text-2xl sm:text-3xl font-bold">$0</span>
                  <span className="text-muted-foreground text-sm">/mo</span>
                </div>
              </div>
              <ul className="space-y-2.5 mb-5 text-sm">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />50 lifetime actions (searches + calculations)</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />10 inventory items max</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />Basic inventory sharing</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />Profit calculator: eBay only</li>
                <li className="flex items-center gap-2 text-muted-foreground"><X className="w-4 h-4 text-red-500 flex-shrink-0" />No price alerts or releases</li>
              </ul>
              <Button onClick={() => handleSelectPlan('free')} variant="outline" className="w-full h-11" disabled={user && currentPlan === 'free'}>
                {user ? (currentPlan === 'free' ? 'Current Plan' : 'Downgrade') : 'Sign Up Free'}
              </Button>
            </div>

            {/* Basic Plan - $4.99/mo or $49/yr */}
            <div className="relative bg-card rounded-xl sm:rounded-2xl border border-border p-5 sm:p-6 transition-all hover:shadow-lg">
              <div className="absolute -top-3 -right-3 px-3 py-1.5 bg-teal-700 text-white text-sm font-bold rounded-full shadow-lg flex items-center gap-1.5">
                <span>✨</span><span>3-day Free Trial</span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-500">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Basic</h3>
                  <div className="flex items-baseline gap-1">
                    {promoApplied && <span className="text-lg text-muted-foreground line-through">${basicPrice.original}</span>}
                    <span className="text-2xl sm:text-3xl font-bold">${billingInterval === 'annual' ? basicPrice.price.toFixed(0) : basicPrice.price.toFixed(2)}</span>
                    <span className="text-muted-foreground text-sm">/{billingInterval === 'annual' ? 'yr' : 'mo'}</span>
                  </div>
                </div>
              </div>
              {billingInterval === 'monthly' ? (
                <div className="flex items-center gap-2 mb-4 p-2 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <span className="text-base font-bold text-foreground">or $49/yr</span>
                  <span className="px-2 py-0.5 bg-emerald-500 text-white text-sm font-bold rounded">-17%</span>
                </div>
              ) : (
                <p className="text-sm font-medium text-muted-foreground mb-4">Just <span className="text-foreground font-bold">${basicPrice.perMonth}/mo</span> billed annually</p>
              )}
              <ul className="space-y-2.5 mb-5 text-sm">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />Unlimited searches</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />Unlimited profit calculations</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />Unlimited inventory items</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />Advanced inventory sharing</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />1 calculator platform (changeable)</li>
                <li className="flex items-center gap-2 text-muted-foreground"><X className="w-4 h-4 text-red-500 flex-shrink-0" />No alerts or releases</li>
              </ul>
              <Button onClick={() => handleSelectPlan('basic')} disabled={checkoutLoading === 'basic' || currentPlan === 'basic'} className="w-full h-11 bg-blue-500 hover:bg-blue-600 text-white">
                {checkoutLoading === 'basic' ? <Loader2 className="w-4 h-4 animate-spin" /> : currentPlan === 'basic' ? 'Current Plan' : 'Start Free Trial'}
              </Button>
            </div>

            {/* Pro Plan - $9.99/mo or $99/yr - POPULAR */}
            <div className="relative bg-card rounded-xl sm:rounded-2xl border-2 border-primary p-5 sm:p-6 transition-all hover:shadow-lg">
              <div className="absolute -top-3 left-4 px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full">POPULAR</div>
              <div className="absolute -top-4 -right-3">
                <div className="px-4 py-2 bg-emerald-700 text-white text-base font-bold rounded-full shadow-lg flex items-center gap-1.5">
                  <span>✨</span><span>7-day Free Trial</span>
                </div>
              </div>
              <div className="flex items-center gap-3 mb-4 mt-2">
                <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
                  <Crown className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Pro</h3>
                  <div className="flex items-baseline gap-1">
                    {promoApplied && <span className="text-lg text-muted-foreground line-through">${proPrice.original}</span>}
                    <span className="text-2xl sm:text-3xl font-bold">${billingInterval === 'annual' ? proPrice.price.toFixed(0) : proPrice.price.toFixed(2)}</span>
                    <span className="text-muted-foreground text-sm">/{billingInterval === 'annual' ? 'yr' : 'mo'}</span>
                  </div>
                </div>
              </div>
              {billingInterval === 'monthly' ? (
                <div className="flex items-center gap-2 mb-4 p-2 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <span className="text-base font-bold text-foreground">or $99/yr</span>
                  <span className="px-2 py-0.5 bg-emerald-500 text-white text-sm font-bold rounded">-17%</span>
                </div>
              ) : (
                <p className="text-sm font-medium text-muted-foreground mb-4">Just <span className="text-foreground font-bold">${proPrice.perMonth}/mo</span> billed annually</p>
              )}
              <ul className="space-y-2.5 mb-5 text-sm">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />Unlimited everything</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />All calculator platforms</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />Price alerts + inventory sharing</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />Release schedule + pre-orders</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0" />CSV exports</li>
              </ul>
              <Button onClick={() => handleSelectPlan('pro')} disabled={checkoutLoading === 'pro' || currentPlan === 'pro'} className="w-full h-11 bg-primary hover:bg-primary/90">
                {checkoutLoading === 'pro' ? <Loader2 className="w-4 h-4 animate-spin" /> : currentPlan === 'pro' ? 'Current Plan' : 'Start Free Trial'}
              </Button>
            </div>

            {/* Lifetime Pro - Launch Special */}
            {lifetimeProStatus?.available && (
              <div className="relative bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 rounded-xl sm:rounded-2xl border-2 border-amber-400 dark:border-amber-600 p-5 sm:p-6 transition-all hover:shadow-xl shadow-amber-200/50 dark:shadow-amber-900/30">
                {/* Launch Special Badge */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold rounded-full shadow-lg flex items-center gap-2 whitespace-nowrap">
                    <Star className="w-4 h-4 fill-white" />
                    <span>LAUNCH SPECIAL</span>
                    <Star className="w-4 h-4 fill-white" />
                  </div>
                </div>
                
                {/* Limited Badge */}
                <div className="absolute -top-3 -right-3">
                  <div className="px-3 py-1.5 bg-red-500 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                    Only {lifetimeProStatus.remaining} left!
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-4 mt-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg">
                    <Gift className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-amber-900 dark:text-amber-100">Lifetime Pro</h3>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl sm:text-4xl font-bold text-amber-600 dark:text-amber-400">${lifetimeProStatus.price}</span>
                      <span className="text-amber-700 dark:text-amber-300 font-medium">one-time</span>
                    </div>
                  </div>
                </div>

                {/* Value Proposition */}
                <div className="mb-4 p-3 bg-white/60 dark:bg-black/20 rounded-xl border border-amber-200 dark:border-amber-700">
                  <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-semibold">Limited Time Offer — Ends Jan 31, 2026</span>
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    Pay once, use forever. Less than 16 months of Pro subscription!
                  </p>
                </div>

                <ul className="space-y-2.5 mb-5 text-sm">
                  <li className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
                    <Check className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span className="font-medium">Full Pro access forever</span>
                  </li>
                  <li className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
                    <Check className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span className="font-medium">All current + future Pro features</span>
                  </li>
                  <li className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
                    <Check className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span className="font-medium">No recurring payments ever</span>
                  </li>
                  <li className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
                    <Check className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span className="font-medium">Early supporter exclusive</span>
                  </li>
                </ul>

                <Button 
                  onClick={handleLifetimeProCheckout} 
                  disabled={lifetimeLoading || currentPlan === 'lifetime_pro' || !lifetimeProStatus?.available}
                  className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-lg shadow-lg"
                >
                  {lifetimeLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : currentPlan === 'lifetime_pro' ? (
                    '✓ You Have Lifetime Pro!'
                  ) : (
                    <>
                      <Gift className="w-5 h-5 mr-2" />
                      Get Lifetime Pro — ${lifetimeProStatus.price}
                    </>
                  )}
                </Button>

                <p className="text-center text-xs text-amber-700 dark:text-amber-400 mt-3">
                  🔒 Secure one-time payment • No subscription required
                </p>
              </div>
            )}

            {/* Lifetime Pro Sold Out State */}
            {lifetimeProStatus && !lifetimeProStatus.available && lifetimeProStatus.reason === 'sold_out' && (
              <div className="relative bg-gray-100 dark:bg-gray-800/50 rounded-xl sm:rounded-2xl border border-gray-300 dark:border-gray-700 p-5 sm:p-6 opacity-75">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="px-4 py-2 bg-gray-500 text-white text-sm font-bold rounded-full">
                    SOLD OUT
                  </div>
                </div>
                <div className="flex items-center gap-3 mb-4 mt-4">
                  <div className="p-3 rounded-xl bg-gray-400 text-white">
                    <Gift className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-600 dark:text-gray-400">Lifetime Pro</h3>
                    <span className="text-gray-500">All {lifetimeProStatus.max} spots claimed!</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 text-center">
                  The Lifetime Pro launch special has sold out. Thank you to all our early supporters!
                </p>
              </div>
            )}
          </div>
        )}

        {/* Compare Link */}
        <div className="text-center mb-6 sm:mb-8">
          <button onClick={scrollToCompare} className="text-primary font-medium hover:underline inline-flex items-center gap-1 text-sm sm:text-base">
            Compare all features<ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Comparison Table */}
        <div id="compare-features" className="max-w-3xl mx-auto mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-bold text-center mb-4 sm:mb-6">Compare All Features</h2>
          <div className="bg-card rounded-xl border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary/50">
                  <th className="text-left p-3 sm:p-4 font-semibold">Feature</th>
                  <th className="text-center p-3 sm:p-4 font-semibold">Free</th>
                  <th className="text-center p-3 sm:p-4 font-semibold text-blue-500">Basic</th>
                  <th className="text-center p-3 sm:p-4 font-semibold text-primary">Pro</th>
                  {lifetimeProStatus?.available && (
                    <th className="text-center p-3 sm:p-4 font-semibold text-amber-600">Lifetime</th>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border">
                  <td className="p-3 sm:p-4">Searches + Calculations</td>
                  <td className="p-3 sm:p-4 text-center">50 lifetime</td>
                  <td className="p-3 sm:p-4 text-center"><Infinity className="w-4 h-4 mx-auto text-blue-500" /></td>
                  <td className="p-3 sm:p-4 text-center"><Infinity className="w-4 h-4 mx-auto text-primary" /></td>
                  {lifetimeProStatus?.available && (
                    <td className="p-3 sm:p-4 text-center"><Infinity className="w-4 h-4 mx-auto text-amber-600" /></td>
                  )}
                </tr>
                <tr className="border-t border-border bg-secondary/20">
                  <td className="p-3 sm:p-4">Inventory Items</td>
                  <td className="p-3 sm:p-4 text-center">10</td>
                  <td className="p-3 sm:p-4 text-center"><Infinity className="w-4 h-4 mx-auto text-blue-500" /></td>
                  <td className="p-3 sm:p-4 text-center"><Infinity className="w-4 h-4 mx-auto text-primary" /></td>
                  {lifetimeProStatus?.available && (
                    <td className="p-3 sm:p-4 text-center"><Infinity className="w-4 h-4 mx-auto text-amber-600" /></td>
                  )}
                </tr>
                <tr className="border-t border-border">
                  <td className="p-3 sm:p-4">Calculator Platforms</td>
                  <td className="p-3 sm:p-4 text-center">eBay only</td>
                  <td className="p-3 sm:p-4 text-center">1 platform</td>
                  <td className="p-3 sm:p-4 text-center text-primary font-medium">All</td>
                  {lifetimeProStatus?.available && (
                    <td className="p-3 sm:p-4 text-center text-amber-600 font-medium">All</td>
                  )}
                </tr>
                <tr className="border-t border-border bg-secondary/20">
                  <td className="p-3 sm:p-4">Inventory Sharing</td>
                  <td className="p-3 sm:p-4 text-center text-muted-foreground">Basic</td>
                  <td className="p-3 sm:p-4 text-center"><Check className="w-4 h-4 mx-auto text-blue-500" /></td>
                  <td className="p-3 sm:p-4 text-center"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                  {lifetimeProStatus?.available && (
                    <td className="p-3 sm:p-4 text-center"><Check className="w-4 h-4 mx-auto text-amber-600" /></td>
                  )}
                </tr>
                <tr className="border-t border-border">
                  <td className="p-3 sm:p-4">Price Alerts</td>
                  <td className="p-3 sm:p-4 text-center"><X className="w-4 h-4 mx-auto text-red-500" /></td>
                  <td className="p-3 sm:p-4 text-center"><X className="w-4 h-4 mx-auto text-red-500" /></td>
                  <td className="p-3 sm:p-4 text-center"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                  {lifetimeProStatus?.available && (
                    <td className="p-3 sm:p-4 text-center"><Check className="w-4 h-4 mx-auto text-amber-600" /></td>
                  )}
                </tr>
                <tr className="border-t border-border bg-secondary/20">
                  <td className="p-3 sm:p-4">Release Schedule</td>
                  <td className="p-3 sm:p-4 text-center"><X className="w-4 h-4 mx-auto text-red-500" /></td>
                  <td className="p-3 sm:p-4 text-center"><X className="w-4 h-4 mx-auto text-red-500" /></td>
                  <td className="p-3 sm:p-4 text-center"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                  {lifetimeProStatus?.available && (
                    <td className="p-3 sm:p-4 text-center"><Check className="w-4 h-4 mx-auto text-amber-600" /></td>
                  )}
                </tr>
                <tr className="border-t border-border">
                  <td className="p-3 sm:p-4">CSV Exports</td>
                  <td className="p-3 sm:p-4 text-center"><X className="w-4 h-4 mx-auto text-red-500" /></td>
                  <td className="p-3 sm:p-4 text-center"><X className="w-4 h-4 mx-auto text-red-500" /></td>
                  <td className="p-3 sm:p-4 text-center"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                  {lifetimeProStatus?.available && (
                    <td className="p-3 sm:p-4 text-center"><Check className="w-4 h-4 mx-auto text-amber-600" /></td>
                  )}
                </tr>
                <tr className="border-t border-border bg-secondary/20">
                  <td className="p-3 sm:p-4 font-semibold">Price</td>
                  <td className="p-3 sm:p-4 text-center font-semibold">$0</td>
                  <td className="p-3 sm:p-4 text-center font-semibold text-blue-500">
                    {billingInterval === 'annual' ? '$49/yr' : '$4.99/mo'}
                  </td>
                  <td className="p-3 sm:p-4 text-center font-semibold text-primary">
                    {billingInterval === 'annual' ? '$99/yr' : '$9.99/mo'}
                  </td>
                  {lifetimeProStatus?.available && (
                    <td className="p-3 sm:p-4 text-center font-semibold text-amber-600">
                      ${lifetimeProStatus.price} once
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto">
          <h2 className="text-lg sm:text-xl font-bold text-center mb-4 sm:mb-6">FAQ</h2>
          <div className="space-y-3 sm:space-y-4">
            {lifetimeProStatus?.available && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 rounded-xl border border-amber-200 dark:border-amber-800 p-4 sm:p-5">
                <h3 className="font-semibold mb-2 text-sm sm:text-base text-amber-900 dark:text-amber-100">What is Lifetime Pro?</h3>
                <p className="text-xs sm:text-sm text-amber-800 dark:text-amber-200">
                  Lifetime Pro is a special launch offer for early supporters. Pay ${lifetimeProStatus.price} once and get full Pro access forever — no recurring payments. Only {lifetimeProStatus.max} spots available, and {lifetimeProStatus.remaining} remain. This offer ends January 31, 2026.
                </p>
              </div>
            )}
            <div className="bg-card rounded-xl border border-border p-4 sm:p-5">
              <h3 className="font-semibold mb-2 text-sm sm:text-base">How does the free trial work?</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Basic plans include a 3-day free trial, Pro plans include a 7-day free trial. You will not be charged until the trial ends. Cancel anytime.
              </p>
            </div>
            <div className="bg-card rounded-xl border border-border p-4 sm:p-5">
              <h3 className="font-semibold mb-2 text-sm sm:text-base">What happens when I hit my Free plan limits?</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Free plan limits reset monthly. When you hit a limit, you will be prompted to upgrade or wait until the next month.
              </p>
            </div>
            <div className="bg-card rounded-xl border border-border p-4 sm:p-5">
              <h3 className="font-semibold mb-2 text-sm sm:text-base">Can I change plans later?</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Yes! You can upgrade, downgrade, or cancel your subscription anytime from your account settings.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
